import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { clientService, transactionService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Minus, TrendingUp, TrendingDown, Euro, Search } from 'lucide-react';

const FundsManagement = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [operationType, setOperationType] = useState('add'); // 'add' or 'remove'
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const data = await clientService.getAll();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les clients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalFunds = clients.reduce((sum, client) => sum + client.balance, 0);

  const filteredClients = clients.filter(
    (client) =>
      client.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (client, type) => {
    setSelectedClient(client);
    setOperationType(type);
    setIsDialogOpen(true);
  };

  const handleTransaction = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un montant valide',
        variant: 'destructive',
      });
      return;
    }

    try {
      const transactionAmount = parseFloat(amount);
      
      const result = await transactionService.create({
        clientId: selectedClient.id,
        type: operationType === 'add' ? 'credit' : 'debit',
        amount: transactionAmount,
        description: description || `${operationType === 'add' ? 'Ajout' : 'Retrait'} de fonds par admin`
      });

      // Update client list with new balance
      const updatedClients = clients.map((client) => {
        if (client.id === selectedClient.id) {
          return { ...client, balance: result.newBalance };
        }
        return client;
      });

      setClients(updatedClients);
      toast({
        title: 'Transaction réussie',
        description: `${operationType === 'add' ? 'Ajout' : 'Retrait'} de ${transactionAmount.toLocaleString()} € pour ${selectedClient.firstName} ${selectedClient.lastName}`,
      });

      setIsDialogOpen(false);
      setAmount('');
      setDescription('');
      setSelectedClient(null);
    } catch (error) {
      console.error('Transaction error:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible d\'effectuer la transaction',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex">
      <AdminSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Gestion des fonds</h1>
          <p className="text-gray-600 mt-1">
            Ajoutez ou retirez des fonds des comptes clients
          </p>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Total Funds Card */}
          <Card className="p-6 mb-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 mb-2">Total des fonds gérés</p>
                <p className="text-4xl font-bold">{totalFunds.toLocaleString()} €</p>
                <p className="text-green-100 mt-2">{clients.length} clients actifs</p>
              </div>
              <div className="p-4 bg-white/20 rounded-lg">
                <Euro className="h-12 w-12" />
              </div>
            </div>
          </Card>

          {/* Search */}
          <Card className="p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher un client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Clients Funds Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Client</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Compte bancaire</TableHead>
                  <TableHead>Solde actuel</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="font-semibold text-purple-700">
                            {client.firstName.charAt(0)}{client.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {client.firstName} {client.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{client.city}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{client.email}</TableCell>
                    <TableCell className="text-gray-600 font-mono text-sm">
                      {client.accountNumber}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-900">
                          {client.balance.toLocaleString()} €
                        </span>
                        {client.balance > 20000 && (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        )}
                        {client.balance < 10000 && (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          className="text-white"
                          style={{ backgroundColor: '#10B981' }}
                          onClick={() => handleOpenDialog(client, 'add')}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleOpenDialog(client, 'remove')}
                        >
                          <Minus className="h-4 w-4 mr-1" />
                          Retirer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>

      {/* Transaction Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {operationType === 'add' ? 'Ajouter des fonds' : 'Retirer des fonds'}
            </DialogTitle>
            <DialogDescription>
              {selectedClient && (
                <span>
                  Client : {selectedClient.firstName} {selectedClient.lastName} (Solde actuel :{' '}
                  {selectedClient.balance.toLocaleString()} €)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Montant (€) *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Raison de la transaction"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {selectedClient && amount && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Nouveau solde :</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(
                    operationType === 'add'
                      ? selectedClient.balance + parseFloat(amount || 0)
                      : selectedClient.balance - parseFloat(amount || 0)
                  ).toLocaleString()}{' '}
                  €
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleTransaction}
              className="text-white"
              style={{
                backgroundColor: operationType === 'add' ? '#10B981' : '#EF4444',
              }}
            >
              {operationType === 'add' ? 'Ajouter' : 'Retirer'} {amount && `${parseFloat(amount).toLocaleString()} €`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundsManagement;
